import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  TextField,
  Button
} from '@mui/material';
import { useGlobal } from '../contexts/GlobalContext';
import { useBuild } from '../contexts/BuildContext';
import FilterTypeRouter from '../filters/FilterTypeRouter';
import FilterListSection from './FilterListSection';
import { handleFilterUpdate } from './filterUtils';

const BoostQuery = ({ filterTypeOptions, source }) => {
  const { mergeCurrentContext } = useGlobal();
  const { searchApi } = useBuild();

  // Main filter configuration state
  const [selectedFilterType, setSelectedFilterType] = useState('');

  // Filter lists
  const [includeFilters, setIncludeFilters] = useState([]);
  const [excludeFilters, setExcludeFilters] = useState([]);
  const [boostFilters, setBoostFilters] = useState([]);

  // Query Parameters state
  const [size, setSize] = useState('100');

  useEffect(() => {
    mergeCurrentContext({
      queryBeingBuilt: {
        includeFilters,
        excludeFilters,
        boostFilters,
        size,
      },
    });
    return () => mergeCurrentContext({ queryBeingBuilt: undefined });
  }, [includeFilters, excludeFilters, boostFilters, size, mergeCurrentContext]);

  const handleFilterTypeChange = (event) => {
    const value = event.target.value;
    setSelectedFilterType(value);
  };

  const handleRemoveFilter = (filterIndex, mode) => {
    if (mode === 'include') {
      setIncludeFilters(prev => prev.filter((_, index) => index !== filterIndex));
    } else if (mode === 'exclude') {
      setExcludeFilters(prev => prev.filter((_, index) => index !== filterIndex));
    } else if (mode === 'boost') {
      setBoostFilters(prev => prev.filter((_, index) => index !== filterIndex));
    }
  };

  const handleFilterUpdateWrapper = (updateData) => {
    handleFilterUpdate(
      updateData,
      includeFilters,
      excludeFilters,
      setIncludeFilters,
      setExcludeFilters,
      boostFilters,
      setBoostFilters
    );
  };

  const handleExecuteQuery = async () => {
    const queryData = {
      size: parseInt(size) || 100,
      include: includeFilters,
      exclude: excludeFilters,
      boost: boostFilters
    };
    console.log('Sending Boost Query:', queryData);
    try {
      const response = await searchApi.generateBoostCandidates(queryData);
      console.log('API Response:', response);
    } catch (error) {
      console.error('Error executing query:', error);
    }
  };


  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Boost Query Configuration
      </Typography>

      <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
        {/* Filter Type Selection */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter Type</InputLabel>
            <Select
              value={selectedFilterType}
              label="Filter Type"
              onChange={handleFilterTypeChange}
            >
              {filterTypeOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* Filter Configuration */}
        <FilterTypeRouter
          filterType={selectedFilterType}
          boostInput={true}
          onUpdate={handleFilterUpdateWrapper}
          source={source}
        />

        {/* Active Filters Display */}
        {(includeFilters.length > 0 || excludeFilters.length > 0 || boostFilters.length > 0) && (
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Active Boosting Conditions
            </Typography>

            <FilterListSection
              title="Must include"
              filters={includeFilters}
              onDelete={(index) => handleRemoveFilter(index, 'include')}
              color="primary"
            />

            <FilterListSection
              title="Should include"
              filters={boostFilters}
              onDelete={(index) => handleRemoveFilter(index, 'boost')}
              color="success"
            />

            <FilterListSection
              title="Exclude"
              filters={excludeFilters}
              onDelete={(index) => handleRemoveFilter(index, 'exclude')}
              color="error"
            />
          </Paper>
        )}

        {/* Query Params Section */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Query Parameters
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Size"
              type="number"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="100"
              helperText="Number of results to return"
              sx={{ maxWidth: 200 }}
            />
          </Box>
        </Paper>

        {/* Execute Query Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleExecuteQuery}
            sx={{ minWidth: 200 }}
          >
            Execute Query
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BoostQuery;

